import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { toast } from 'sonner';
import {
  Button,
  Checkbox,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@staff-app/ui';
import { STAFF_PROXY_PUSH_TO_SCREENS } from '@staff-app/graphql-client';
import { useAgentDevices } from '@/features/agent-devices/hooks/useAgentDevices';

interface Props {
  /** Content id (asset/playlist/schedule) to push; dialog is open when non-null. */
  contentId: string | null;
  onOpenChange: (open: boolean) => void;
}

interface PushResult {
  staffProxyPushToScreens: string;
}

/**
 * Push a customer device's current content to the staff member's own testing
 * devices ("My devices"). Pick one / several / all, then loop the
 * staffProxyPushToScreens mutation (type auto-detected) → open-api pushes it
 * to each device's screen.
 *
 * NOTE: open-api impersonates each testing device's OWN account, so a
 * customer's content (different account) will be rejected upstream — this works
 * when the content is in the testing device's account.
 */
export function PushToMyDevicesDialog({ contentId, onOpenChange }: Props) {
  const open = contentId !== null;
  const { devices, loading } = useAgentDevices();
  const myDevices = useMemo(() => devices.filter((d) => d.found), [devices]);

  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Fresh selection each time the dialog opens for a content id.
  useEffect(() => {
    if (open) setSelected(new Set());
  }, [open, contentId]);

  const [setMutation, { loading: pushing }] = useMutation<PushResult>(STAFF_PROXY_PUSH_TO_SCREENS, {
    refetchQueries: ['staffAgentDevices'],
  });

  const allChecked = myDevices.length > 0 && myDevices.every((d) => selected.has(d.deviceId));
  const someChecked = myDevices.some((d) => selected.has(d.deviceId));
  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = () =>
    setSelected(() => (allChecked ? new Set() : new Set(myDevices.map((d) => d.deviceId))));

  const selectedIds = myDevices.map((d) => d.deviceId).filter((id) => selected.has(id));

  const handlePush = async () => {
    if (!contentId || !selectedIds.length) return;
    // Push each independently so one failure doesn't skip the rest.
    const results = await Promise.allSettled(
      selectedIds.map((deviceId) => setMutation({ variables: { deviceId, contentId } })),
    );
    const failed = results.filter((r) => r.status === 'rejected').length;
    const succeeded = selectedIds.length - failed;

    if (failed === 0) {
      toast.success(`Pushed to ${succeeded} device${succeeded === 1 ? '' : 's'}`, {
        description: 'Content assigned and pushed to the screens (type auto-detected).',
      });
      onOpenChange(false);
      return;
    }
    const firstErr = results.find((r) => r.status === 'rejected') as
      | PromiseRejectedResult
      | undefined;
    const msg = (firstErr?.reason as { message?: string })?.message ?? 'Unknown error';
    toast.error(`Pushed to ${succeeded} of ${selectedIds.length}`, {
      description: `${failed} failed: ${msg}`,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="push-to-my-devices-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{'Push to my devices'}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3 py-2">
          <p className="text-sm text-muted-foreground">
            {'Assign content '}
            <span className="font-mono text-xs text-foreground">{contentId}</span>
            {' to your testing devices and push it to their screens.'}
          </p>

          {loading && myDevices.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">{'Loading your devices…'}</p>
          ) : myDevices.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">
              {'No testing devices yet — add some on My Devices.'}
            </p>
          ) : (
            <div className="flex flex-col gap-1 rounded-md border p-2">
              <label className="flex cursor-pointer items-center gap-2 border-b pb-2 text-sm font-medium">
                <Checkbox
                  checked={allChecked ? true : someChecked ? 'indeterminate' : false}
                  onCheckedChange={() => toggleAll()}
                  aria-label="Select all devices"
                  data-testid="push-select-all"
                />
                {'Select all'}
              </label>
              <div className="max-h-64 overflow-y-auto">
                {myDevices.map((d) => (
                  <label
                    key={d.deviceId}
                    className="flex cursor-pointer items-center gap-2 rounded-sm px-1 py-1.5 text-sm hover:bg-accent/50"
                  >
                    <Checkbox
                      checked={selected.has(d.deviceId)}
                      onCheckedChange={() => toggle(d.deviceId)}
                      aria-label={d.device?.deviceName ?? d.deviceId}
                      data-testid="push-device-check"
                    />
                    <span className="min-w-0 flex-1 truncate">
                      {d.device?.deviceName ?? d.deviceId}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">{'Cancel'}</Button>
          </DialogClose>
          <Button
            onClick={handlePush}
            disabled={pushing || selectedIds.length === 0}
            data-testid="push-to-my-devices-submit"
          >
            {pushing ? 'Pushing…' : selectedIds.length ? `Push to ${selectedIds.length}` : 'Push'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
